import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Fade,
  Paper,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  alpha,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { colors } from '../theme';
import { stocksAPI, aiRecommendationsAPI } from '../services/api';
import StockDetailSkeleton from '../components/stocks/StockDetailSkeleton';
import TradingViewChart from '../components/TradingViewChart';
import { getMarketStageLabel } from '../utils/marketStageLabels';

const STYLE_LABEL_KEYS = {
  FLOW_LEADERS: 'vnstock.recommendations.styles.flowLeaders',
  QUALITY_GROWTH: 'vnstock.recommendations.styles.qualityGrowth',
  VALUE_WITH_CONFIRMATION: 'vnstock.recommendations.styles.valueConfirmation',
  TURNAROUND: 'vnstock.recommendations.styles.turnaround',
  ACCUMULATION_BREAKOUT: 'vnstock.recommendations.styles.accumulationBreakout',
  REVERSAL: 'vnstock.recommendations.styles.reversal',
  SUPER_STOCK: 'vnstock.recommendations.styles.superStock',
  PRICE_ACTION: 'vnstock.recommendations.styles.priceAction',
};

const getLocalizedValue = (value, lang = 'vi') => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return value[lang] || value.vi || value.en || '';
  return '';
};

const getVisibleMarketStageOverlay = (overlay, lang = 'vi') => {
  const text = getLocalizedValue(overlay?.explanation, lang);
  const pct = overlay?.adjustment_pct;
  const visible = typeof pct === 'number' && Math.abs(pct) >= 2.5 && Boolean(text);
  return {
    visible,
    text,
    pct,
    positive: typeof pct === 'number' ? pct >= 0 : true,
  };
};

const getCompactMarketStageSummary = (marketStage, lang = 'vi') => {
  const exposure = marketStage?.exposure_target_pct;
  const exposureText = typeof exposure === 'number' ? `${exposure.toFixed(1)}%` : null;

  if (lang === 'vi') {
    let guidance = 'Ưu tiên các setup đi đúng pha hiện tại';
    if (typeof exposure === 'number') {
      if (exposure >= 75) guidance = 'Có thể chủ động hơn với các mã mạnh đúng pha';
      else if (exposure >= 55) guidance = 'Ưu tiên setup mạnh nhưng vẫn giữ chọn lọc';
      else if (exposure >= 35) guidance = 'Giữ chọn lọc cao, chỉ theo các setup rõ ràng nhất';
      else guidance = 'Thiên về phòng thủ và chờ xác nhận rõ hơn';
    }
    return exposureText ? `${guidance}. Tỷ trọng cổ phiếu tham khảo khoảng ${exposureText}.` : guidance;
  }

  let guidance = 'Favor setups that align with the current phase';
  if (typeof exposure === 'number') {
    if (exposure >= 75) guidance = 'You can lean in more on strong names aligned with this phase';
    else if (exposure >= 55) guidance = 'Prefer stronger setups while keeping selectivity high';
    else if (exposure >= 35) guidance = 'Stay selective and focus only on the clearest setups';
    else guidance = 'Stay defensive and wait for cleaner confirmation';
  }
  return exposureText ? `${guidance}. Suggested equity allocation is around ${exposureText}.` : guidance;
};

const DetailCard = ({ title, subtitle, action, children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 2,
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      background: '#fff',
      ...sx,
    }}
  >
    <Box
      sx={{
        px: 1.7,
        py: 1.15,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
        borderBottom: '1px solid #eef2f7',
        background: '#fcfcfd',
      }}
    >
      <Box>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ fontSize: '0.68rem', color: '#64748b', mt: 0.18, lineHeight: 1.35 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action ? <Box>{action}</Box> : null}
    </Box>
    <Box sx={{ p: 1.45 }}>
      {children}
    </Box>
  </Paper>
);

const StockDetail = () => {
  const { ticker } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'vi';
  const marketStageAccent = '#2563eb';
  const getStyleLabel = useCallback(
    (style) => (style ? t(STYLE_LABEL_KEYS[style] || style, style.replace(/_/g, ' ')) : ''),
    [t]
  );

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stockData, setStockData] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [aiRecommendation, setAIRecommendation] = useState(null);
  const [error, setError] = useState(null);
  const [chartType, setChartType] = useState('candlestick');
  const [showVolume, setShowVolume] = useState(true);
  const [showIndicators, setShowIndicators] = useState(true);
  const [chartDays, setChartDays] = useState(365);
  const [chartLoading, setChartLoading] = useState(false);

  const fetchStockData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      setRecommendations(null);
      setAIRecommendation(null);

      try {
        const [stockResponse, priceResponse] = await Promise.all([
          stocksAPI.getStock(ticker),
          stocksAPI.getStockPrices(ticker, 365)
        ]);

        setStockData(stockResponse.data);
        const sortedPrices = [...(priceResponse.data || [])].sort((a, b) => {
          const dateA = new Date(a.date || a.time || a.timestamp || a.trading_date || 0).getTime();
          const dateB = new Date(b.date || b.time || b.timestamp || b.trading_date || 0).getTime();
          return dateB - dateA;
        });
        setPriceHistory(sortedPrices);

        setLoading(false);
        setRefreshing(false);

      } catch (criticalErr) {
        throw criticalErr;
      }

      Promise.allSettled([
        stocksAPI.getTickerRecommendation(ticker),
        aiRecommendationsAPI.getAIRecommendationForTicker(ticker),
      ]).then(([recResult, aiResult]) => {
        if (recResult.status === 'fulfilled') {
          setRecommendations(recResult.value.data);
        }
        if (aiResult.status === 'fulfilled') {
          setAIRecommendation(aiResult.value.data);
        }
      });

    } catch (err) {
      console.error('Error fetching stock data:', err);
      console.error('Full error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.detail || err.message);
      setLoading(false);
      setRefreshing(false);
    }
  }, [ticker]);

  const handleRefresh = () => {
    fetchStockData(true);
  };

  const handleBack = useCallback(() => {
    const historyIdx = window.history.state?.idx;
    if (location.state?.from && typeof historyIdx === 'number' && historyIdx > 0) {
      navigate(-1);
      return;
    }

    if (typeof location.state?.from === 'string' && location.state.from) {
      navigate(location.state.from);
      return;
    }

    navigate('/stocks');
  }, [location.state, navigate]);

  useEffect(() => {
    fetchStockData(false);
  }, [fetchStockData]);

  const getLocalizedText = useCallback((value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(getLocalizedText).filter(Boolean).join(', ');
    if (typeof value === 'object') return value[currentLang] || value.vi || value.en || '';
    return String(value);
  }, [currentLang]);

  const formatNumber = useCallback((num, decimals = 2) => {
    if (!num && num !== 0) return 'N/A';
    return num.toLocaleString(currentLang === 'vi' ? 'vi-VN' : 'en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }, [currentLang]);

  // Format stock price (no thousand separator, just decimal)
  const normalizePrice = (value) => {
    if (!value && value !== 0) return null;
    // Heuristic: if value looks like VND, convert to thousands (handle negatives too)
    return Math.abs(value) >= 1000 ? value / 1000 : value;
  };

  const formatStockPrice = (num, decimals = 2) => {
    const normalized = normalizePrice(num);
    if (normalized === null) return 'N/A';
    return normalized.toFixed(decimals);
  };

  const formatMarketCap = useCallback((value) => {
    if (!value && value !== 0) return { value: 'N/A', unit: '' };

    // market_cap_bn is already in billions; keep raw number and format only
    return { value: formatNumber(value, 1), unit: 'tỷ' };
  }, [formatNumber]);

  const formatVolume = useCallback((value) => {
    if (!value && value !== 0) return 'N/A';
    if (value >= 1000000) {
      return `${formatNumber(value / 1000000, 1)}M`;
    } else if (value >= 1000) {
      return `${formatNumber(value / 1000, 1)}K`;
    }
    return formatNumber(value, 0);
  }, [formatNumber]);

  const getSignalRawValue = useCallback((key) => {
    const signal = recommendations?.signals?.[key];
    return signal?.value !== undefined ? signal.value : signal;
  }, [recommendations]);

  const signalAliases = useMemo(() => ({
    rs_12m: { vi: 'RS 12 tháng', en: '12M relative strength' },
    rs_3m: { vi: 'RS 3 tháng', en: '3M relative strength' },
    rs_20: { vi: 'RS 20 phiên', en: '20-session relative strength' },
    rs_mix: { vi: 'RS tổng hợp', en: 'Composite RS' },
    return_1d: { vi: 'Tăng giảm phiên', en: 'Daily change' },
    return_5d: { vi: 'Hiệu quả 5 phiên', en: '5-session outcome' },
    return_20d: { vi: 'Hiệu quả 20 phiên', en: '20-session outcome' },
    return_60d: { vi: 'Hiệu quả 60 phiên', en: '60-session outcome' },
    dist_from_ma50: { vi: 'Khoảng cách tới MA50', en: 'Distance from MA50' },
    pivot_distance: { vi: 'Khoảng cách tới pivot', en: 'Distance to pivot' },
    dist_to_pivot: { vi: 'Khoảng cách tới pivot', en: 'Distance to pivot' },
    trend_quality: { vi: 'Chất lượng xu hướng', en: 'Trend quality' },
    trend_slope: { vi: 'Độ dốc xu hướng', en: 'Trend slope' },
    base_tightness: { vi: 'Độ chặt nền', en: 'Base tightness' },
    recent_base_tightness: { vi: 'Độ chặt nền mới', en: 'New-base tightness' },
    recent_base_distance: { vi: 'Khoảng cách tới pivot nền mới', en: 'Distance to new-base pivot' },
    recent_base_quality: { vi: 'Chất lượng nền mới', en: 'New-base quality' },
    recent_base_window: { vi: 'Số phiên nền mới', en: 'New-base length' },
    accumulation: { vi: 'Mức tích lũy', en: 'Accumulation' },
    accumulation_score: { vi: 'Điểm tích lũy', en: 'Accumulation score' },
    breakout_readiness: { vi: 'Mức sẵn sàng bứt phá', en: 'Breakout readiness' },
    breakout_50: { vi: 'Mức vượt đỉnh 50 phiên', en: '50-session breakout level' },
    high_quality_base: { vi: 'Nền giá chất lượng cao', en: 'High-quality base' },
    range_tightness: { vi: 'Độ chặt nền giá', en: 'Range tightness' },
    volatility_contraction: { vi: 'Co hẹp biến động', en: 'Volatility contraction' },
    volatility_20: { vi: 'Biến động 20 phiên', en: '20-session volatility' },
    volatility: { vi: 'Biến động ngắn hạn', en: 'Short-term volatility' },
    volume_ratio: { vi: 'Tỷ lệ khối lượng', en: 'Volume ratio' },
    up_volume_ratio: { vi: 'Tỷ lệ khối lượng phiên tăng', en: 'Up-volume ratio' },
    vol_confirm: { vi: 'Xác nhận khối lượng', en: 'Volume confirmation' },
    vol_zscore_20: { vi: 'Đột biến khối lượng 20 phiên', en: '20-session volume z-score' },
    foreign_flow: { vi: 'Dòng tiền ngoại', en: 'Foreign flow' },
    foreign_flow_change: { vi: 'Thay đổi dòng tiền ngoại', en: 'Foreign-flow change' },
    market_down_resilience: { vi: 'Độ lì khi thị trường giảm', en: 'Resilience on market down days' },
    risk: { vi: 'Rủi ro tổng hợp', en: 'Composite risk' },
    overextended_penalty: { vi: 'Giá đã kéo xa chưa', en: 'How far price is from the base' },
    trap_risk: { vi: 'Rủi ro bull trap', en: 'Bull-trap risk' },
    drawdown: { vi: 'Mức giảm từ đỉnh gần', en: 'Recent drawdown' },
    max_drawdown_60: { vi: 'Giảm sâu nhất 60 phiên', en: 'Max drawdown over 60 sessions' },
    roe: { vi: 'ROE', en: 'ROE' },
    growth: { vi: 'Tăng trưởng doanh thu', en: 'Revenue growth' },
    growth_change: { vi: 'Độ đổi chiều tăng trưởng', en: 'Growth inflection' },
    margin: { vi: 'Biên lợi nhuận', en: 'Net margin' },
    leverage: { vi: 'Đòn bẩy tài chính', en: 'Leverage' },
    pe_ratio: { vi: 'P/E', en: 'P/E' },
    pb_ratio: { vi: 'P/B', en: 'P/B' },
    signal_types: { vi: 'Kiểu tín hiệu xác nhận', en: 'Confirmation signal types' },
    signal_count: { vi: 'Số tín hiệu đồng thuận', en: 'Supporting signal count' },
    signal_strength: { vi: 'Độ mạnh tín hiệu', en: 'Signal strength' },
    bull_reversal: { vi: 'Trạng thái đảo chiều tăng', en: 'Bullish reversal state' },
    bear_reversal: { vi: 'Trạng thái đảo chiều giảm', en: 'Bearish reversal state' },
    stage: { vi: 'Giai đoạn setup', en: 'Setup stage' },
    setup_type: { vi: 'Kiểu setup', en: 'Setup type' },
  }), []);

  const stateAliases = useMemo(() => ({
    EARLY_ENTRY: { vi: 'Vào sớm', en: 'Early entry' },
    CONFIRMED: { vi: 'Đã xác nhận', en: 'Confirmed' },
    WATCH: { vi: 'Đang theo dõi', en: 'On watch' },
    NONE: { vi: 'Chưa có tín hiệu', en: 'No signal yet' },
    ACTION_SELL: { vi: 'Cảnh báo đảo chiều giảm', en: 'Bearish reversal warning' },
    ACTION_STRONG: { vi: 'Cảnh báo giảm mạnh', en: 'Strong bearish warning' },
    BREAKOUT: { vi: 'Breakout', en: 'Breakout' },
    STEALTH: { vi: 'Tích lũy kín', en: 'Stealth accumulation' },
    PREBREAK: { vi: 'Sát điểm bứt phá', en: 'Pre-breakout' },
    PULLBACK: { vi: 'Nhịp kéo về', en: 'Pullback' },
    OTHER: { vi: 'Khác', en: 'Other' },
    FAILED_BREAKDOWN: { vi: 'Rũ bỏ thất bại rồi hồi lại', en: 'Failed breakdown recovery' },
    WASHOUT_RECOVERY: { vi: 'Rũ bỏ rồi hồi phục', en: 'Washout recovery' },
    TIGHT_BASE_RECLAIM: { vi: 'Siết nền và lấy lại hỗ trợ', en: 'Tight-base reclaim' },
    SUPPORT_TEST_SUCCESS: { vi: 'Test hỗ trợ thành công', en: 'Support test succeeded' },
    RS_FLIP: { vi: 'RS đảo chiều', en: 'Relative strength flipped' },
    FAILED_BREAKOUT: { vi: 'Breakout thất bại', en: 'Failed breakout' },
  }), []);

  const humanizeSignalIdentifier = useCallback((value) => {
    const acronyms = {
      rs: 'RS',
      ma50: 'MA50',
      ma20: 'MA20',
      ma10: 'MA10',
      roe: 'ROE',
      roa: 'ROA',
      pe: 'P/E',
      pb: 'P/B',
      cmf: 'CMF',
      vol: 'Volume',
    };
    return String(value)
      .split('_')
      .filter(Boolean)
      .map((part) => acronyms[part.toLowerCase()] || (part.length <= 3 ? part.toUpperCase() : `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`))
      .join(' ');
  }, []);

  const getSignalLabel = useCallback((key) => {
    const alias = signalAliases[key];
    return (currentLang === 'vi' ? alias?.vi : alias?.en) || humanizeSignalIdentifier(key);
  }, [currentLang, humanizeSignalIdentifier, signalAliases]);

  const formatSignalStateValue = useCallback((key, raw) => {
    if (Array.isArray(raw)) {
      return raw.map((entry) => {
        const text = String(entry || '');
        const match = text.match(/^([A-Z0-9_]+)\s*\(S(-?\d+(?:\.\d+)?)\)$/);
        if (!match) {
          const alias = stateAliases[text];
          return (currentLang === 'vi' ? alias?.vi : alias?.en) || humanizeSignalIdentifier(text);
        }
        const [, type, strength] = match;
        const alias = stateAliases[type];
        const label = (currentLang === 'vi' ? alias?.vi : alias?.en) || humanizeSignalIdentifier(type);
        return `${label} (S${strength})`;
      }).join(', ');
    }
    if (typeof raw === 'string') {
      const alias = stateAliases[raw];
      return (currentLang === 'vi' ? alias?.vi : alias?.en) || humanizeSignalIdentifier(raw);
    }
    return String(raw);
  }, [currentLang, humanizeSignalIdentifier, stateAliases]);

  const getSignalTone = useCallback((key, raw) => {
    if (raw == null) return { color: '#64748b', bg: '#f8fafc', sentiment: 'neutral' };
    if (typeof raw === 'string' || Array.isArray(raw)) {
      if (key === 'bull_reversal' && raw !== 'NONE' && raw !== 'WATCH') {
        return { color: colors.success.main, bg: alpha(colors.success.main, 0.08), sentiment: 'positive' };
      }
      if (key === 'bear_reversal' && raw !== 'NONE' && raw !== 'WATCH') {
        return { color: colors.error.main, bg: alpha(colors.error.main, 0.08), sentiment: 'negative' };
      }
      return { color: '#475569', bg: '#f8fafc', sentiment: 'neutral' };
    }

    const positive = { color: colors.success.main, bg: alpha(colors.success.main, 0.08), sentiment: 'positive' };
    const negative = { color: colors.error.main, bg: alpha(colors.error.main, 0.08), sentiment: 'negative' };
    const neutral = { color: '#64748b', bg: '#f8fafc', sentiment: 'neutral' };

    switch (key) {
      case 'overextended_penalty':
      case 'trap_risk':
      case 'risk':
      case 'risk_penalty':
      case 'leverage':
        if (raw <= 10 || raw <= 1 || raw <= 25) return positive;
        if (raw <= 18 || raw <= 2 || raw <= 45) return neutral;
        return negative;
      case 'volatility':
      case 'volatility_20':
      case 'drawdown':
      case 'max_drawdown_60':
        if (raw <= 0.3 || raw <= 15 || raw >= -10) return positive;
        if (raw <= 0.5 || raw <= 25 || raw >= -20) return neutral;
        return negative;
      case 'return_1d':
      case 'return_5d':
      case 'return_20d':
      case 'return_60d':
      case 'rs_12m':
      case 'rs_3m':
      case 'rs_20':
      case 'rs_mix':
      case 'foreign_flow':
      case 'foreign_flow_change':
      case 'growth':
      case 'growth_change':
      case 'margin':
      case 'roe':
      case 'roa':
      case 'breakout_readiness':
      case 'accumulation':
      case 'accumulation_score':
      case 'trend_quality':
      case 'signal_strength':
      case 'signal_count':
      case 'entry_quality':
      case 'pivot_score':
        return raw > 0 ? positive : raw < 0 ? negative : neutral;
      case 'dist_from_ma50':
        if (raw >= 0) return positive;
        if (raw >= -2) return neutral;
        return negative;
      case 'pivot_distance':
      case 'dist_to_pivot':
      case 'recent_base_distance':
        if (raw >= -2 && raw <= 2) return positive;
        if (raw >= -5 && raw <= 4) return neutral;
        return negative;
      default:
        return raw > 0 ? positive : raw < 0 ? negative : neutral;
    }
  }, []);

  const formatSignalValue = useCallback((key, raw) => {
    if (raw == null) return '-';
    if (typeof raw === 'string' || Array.isArray(raw)) return formatSignalStateValue(key, raw);
    if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';

    const pctKeys = new Set([
      'return_1d', 'return_5d', 'return_20d', 'return_60d',
      'dist_from_ma50', 'pivot_distance', 'dist_to_pivot', 'drawdown', 'max_drawdown_60',
      'volatility_20', 'breakout_50',
    ]);
    const ratioKeys = new Set(['volume_ratio', 'up_volume_ratio', 'vol_zscore_20']);

    if (pctKeys.has(key)) return `${raw > 0 ? '+' : ''}${Number(raw).toFixed(2)}%`;
    if (ratioKeys.has(key)) return `${Number(raw).toFixed(2)}x`;
    if (key === 'recent_base_window' || key === 'signal_count') return `${Math.round(raw)}`;
    if (key === 'signal_strength') return `${Number(raw).toFixed(1)} / 4`;
    return Number(raw).toFixed(Math.abs(raw) >= 100 ? 0 : 2);
  }, [formatSignalStateValue]);

  const stripTechnicalNoise = useCallback((text) => {
    if (!text || typeof text !== 'string') return text;
    return text
      .replace(/\s*\((?=[^)]*\d)[^)]*\)/g, '')
      .replace(/\s*[+-]?\d+(?:[.,]\d+)?%/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }, []);

  const priceChange = useMemo(() => {
    if (!priceHistory || priceHistory.length < 1) return { change: 0, percent: 0 };
    const latest = priceHistory[0];
    if (!latest) return { change: 0, percent: 0 };
    
    let basePrice;
    if (latest.referencePrice) {
      basePrice = latest.referencePrice;
    } else {
      if (priceHistory.length < 2) return { change: 0, percent: 0 };
      const previous = priceHistory[1];
      if (!previous) return { change: 0, percent: 0 };
      basePrice = previous.close;
    }
    
    const change = latest.close - basePrice;
    const percent = (change / basePrice) * 100;
    return { change, percent };
  }, [priceHistory]);

  const latestPrice = useMemo(() => 
    priceHistory[0]?.close || stockData?.latest_price || 0,
    [priceHistory, stockData]
  );

  const latestBar = useMemo(() => priceHistory?.[0] || null, [priceHistory]);

  const priceColor = useMemo(() => {
    if (priceChange.change > 0) return colors.stock.positive;
    if (priceChange.change < 0) return colors.stock.negative;
    return colors.stock.reference;
  }, [priceChange]);

  const getPriceColor = useCallback(() => priceColor, [priceColor]);

  const latestDateLabel = useMemo(() => {
    const raw = latestBar?.date || latestBar?.time || latestBar?.timestamp || stockData?.latest_date;
    if (!raw) return 'N/A';
    const dateValue = new Date(raw);
    if (Number.isNaN(dateValue.getTime())) return raw;
    return new Intl.DateTimeFormat(currentLang === 'vi' ? 'vi-VN' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(dateValue);
  }, [currentLang, latestBar, stockData]);

  const marketContext = useMemo(() => {
    if (!priceHistory?.length) {
      return {
        high20: null,
        low20: null,
        return5d: null,
        return20d: null,
        avgVol20: stockData?.avg_volume_20d || null,
        volumeRatio: null,
        rangePosition: null,
      };
    }

    const recent20 = priceHistory.slice(0, 20);
    const closes20 = recent20.map((item) => Number(item.close)).filter((item) => Number.isFinite(item));
    const latestClose = Number(priceHistory[0]?.close);
    const high20 = closes20.length ? Math.max(...closes20) : null;
    const low20 = closes20.length ? Math.min(...closes20) : null;

    const close5 = priceHistory[4]?.close;
    const close20 = priceHistory[19]?.close;
    const return5d = close5 ? ((latestClose / close5) - 1) * 100 : null;
    const return20d = close20 ? ((latestClose / close20) - 1) * 100 : null;

    const avgVol20 = stockData?.avg_volume_20d || (recent20.length
      ? recent20.reduce((sum, item) => sum + (Number(item.volume) || 0), 0) / recent20.length
      : null);

    const latestVolume = Number(latestBar?.volume || stockData?.latest_volume);
    const volumeRatio = avgVol20 ? latestVolume / avgVol20 : null;

    const rangePosition = (high20 != null && low20 != null && high20 !== low20)
      ? ((latestClose - low20) / (high20 - low20)) * 100
      : null;

    return { high20, low20, return5d, return20d, avgVol20, volumeRatio, rangePosition };
  }, [latestBar, priceHistory, stockData]);

  const setupSummary = useMemo(() => {
    const score = recommendations?.score;
    if (score == null) {
      return {
        label: currentLang === 'vi' ? 'Chưa có trong top recommendation hiện tại' : 'Not in the current top recommendation set',
        color: '#64748b',
        bg: '#f8fafc',
      };
    }
    if (score >= 80) {
      return {
        label: currentLang === 'vi' ? 'Setup đang khá nổi bật' : 'Setup looks strong',
        color: colors.success.main,
        bg: alpha(colors.success.main, 0.08),
      };
    }
    if (score >= 65) {
      return {
        label: currentLang === 'vi' ? 'Setup ở mức theo dõi tích cực' : 'Setup is worth active monitoring',
        color: colors.warning.main,
        bg: alpha(colors.warning.main, 0.08),
      };
    }
    return {
      label: currentLang === 'vi' ? 'Setup chưa thật sự nổi bật' : 'Setup is not standing out yet',
      color: '#64748b',
      bg: '#f8fafc',
    };
  }, [currentLang, recommendations]);

  const recommendationReasons = useMemo(() => {
    const items = recommendations?.reasons || [];
    return items
      .map((item) => stripTechnicalNoise(getLocalizedText(item)))
      .filter(Boolean)
      .slice(0, 5);
  }, [getLocalizedText, recommendations, stripTechnicalNoise]);

  const marketStageSummary = useMemo(
    () => getCompactMarketStageSummary(recommendations?.marketStage, currentLang),
    [currentLang, recommendations]
  );

  const marketStageOverlay = useMemo(
    () => getVisibleMarketStageOverlay(recommendations?.marketStageOverlay, currentLang),
    [currentLang, recommendations]
  );

  const aiExplanations = useMemo(() => {
    if (!aiRecommendation?.explanations) return [];
    return Object.entries(aiRecommendation.explanations)
      .map(([style, exp]) => ({
        style,
        explanation: getLocalizedText(exp?.explanation),
      }))
      .filter((item) => item.explanation);
  }, [aiRecommendation, getLocalizedText]);

  const signalGroups = useMemo(() => {
    const signals = recommendations?.signals || {};
    const setupKeys = new Set([
      'stage', 'setup_type', 'signal_types', 'signal_count', 'signal_strength',
      'bull_reversal', 'bear_reversal', 'trend_quality', 'trend_slope',
      'base_tightness', 'recent_base_window', 'recent_base_tightness',
      'recent_base_distance', 'recent_base_quality', 'breakout_readiness',
      'pivot_distance', 'dist_to_pivot', 'entry_quality', 'pivot_score',
      'range_tightness', 'high_quality_base', 'accumulation', 'accumulation_score',
    ]);
    const momentumKeys = new Set([
      'rs_12m', 'rs_3m', 'rs_20', 'rs_mix', 'return_1d', 'return_5d', 'return_20d',
      'return_60d', 'volume_ratio', 'up_volume_ratio', 'vol_confirm', 'vol_zscore_20',
      'foreign_flow', 'foreign_flow_change', 'market_down_resilience', 'cmf_10',
      'growth', 'growth_change', 'margin', 'roe',
    ]);
    const riskKeys = new Set([
      'risk', 'risk_penalty', 'overextended_penalty', 'trap_risk', 'drawdown',
      'max_drawdown_60', 'volatility', 'volatility_20', 'dist_from_ma50',
      'leverage', 'pe_ratio', 'pb_ratio',
    ]);

    const groups = [
      { key: 'setup', title: currentLang === 'vi' ? 'Cấu trúc setup' : 'Setup structure', items: [] },
      { key: 'momentum', title: currentLang === 'vi' ? 'Động lượng và dòng tiền' : 'Momentum and flow', items: [] },
      { key: 'risk', title: currentLang === 'vi' ? 'Rủi ro và độ bền' : 'Risk and durability', items: [] },
      { key: 'other', title: currentLang === 'vi' ? 'Tín hiệu khác' : 'Other signals', items: [] },
    ];
    const byKey = Object.fromEntries(groups.map((group) => [group.key, group]));

    Object.entries(signals).forEach(([key, value]) => {
      const raw = value?.value !== undefined ? value.value : value;
      const item = {
        key,
        label: getSignalLabel(key),
        displayValue: formatSignalValue(key, raw),
        tone: getSignalTone(key, raw),
      };

      if (setupKeys.has(key)) byKey.setup.items.push(item);
      else if (momentumKeys.has(key)) byKey.momentum.items.push(item);
      else if (riskKeys.has(key)) byKey.risk.items.push(item);
      else byKey.other.items.push(item);
    });

    return groups.filter((group) => group.items.length > 0);
  }, [currentLang, formatSignalValue, getSignalLabel, getSignalTone, recommendations]);

  const setupEvidence = useMemo(() => ([
    {
      label: t('vnstock.detail.volumeRatio', 'Vol/TB20'),
      value: marketContext.volumeRatio != null ? `${marketContext.volumeRatio.toFixed(2)}x` : 'N/A',
      accent: marketContext.volumeRatio >= 1 ? colors.success.main : '#0f172a',
    },
    {
      label: t('vnstock.detail.priceVsMA50', 'Vị trí so với MA50'),
      value: getSignalRawValue('dist_from_ma50') != null
        ? `${getSignalRawValue('dist_from_ma50') > 0 ? '+' : ''}${Number(getSignalRawValue('dist_from_ma50')).toFixed(2)}%`
        : 'N/A',
      accent: getSignalRawValue('dist_from_ma50') > 0 ? colors.success.main : colors.error.main,
    },
    {
      label: t('vnstock.detail.return20d', 'Hiệu quả 20 phiên'),
      value: marketContext.return20d != null ? `${marketContext.return20d > 0 ? '+' : ''}${marketContext.return20d.toFixed(2)}%` : 'N/A',
      accent: marketContext.return20d >= 0 ? colors.success.main : colors.error.main,
    },
  ]), [getSignalRawValue, marketContext, t]);

  const heroReasons = useMemo(
    () => recommendationReasons.slice(0, 2),
    [recommendationReasons]
  );

  const financialSnapshot = useMemo(() => {
    const safeStockData = stockData || {};
    const marketCap = formatMarketCap(safeStockData.market_cap_bn);

    return [
      { label: t('vnstock.detail.marketCap', 'Vốn hóa'), value: `${marketCap.value}${marketCap.unit ? ` ${marketCap.unit}` : ''}`, color: colors.purple.main },
      { label: 'ROE', value: safeStockData.roe != null ? `${formatNumber(safeStockData.roe, 2)}%` : 'N/A', color: safeStockData.roe > 15 ? colors.success.main : '#0f172a' },
      { label: 'P/E', value: safeStockData.pe != null ? formatNumber(safeStockData.pe, 2) : 'N/A', color: '#0f172a' },
      { label: 'P/B', value: safeStockData.pb != null ? formatNumber(safeStockData.pb, 2) : 'N/A', color: '#0f172a' },
      { label: 'ROA', value: safeStockData.roa != null ? `${formatNumber(safeStockData.roa, 2)}%` : 'N/A', color: safeStockData.roa > 5 ? colors.success.main : '#0f172a' },
      { label: 'D/E', value: safeStockData.debt_to_equity != null ? formatNumber(safeStockData.debt_to_equity, 2) : 'N/A', color: safeStockData.debt_to_equity > 2 ? colors.error.main : '#0f172a' },
      { label: 'EPS', value: safeStockData.eps != null ? formatNumber(safeStockData.eps, 0) : 'N/A', color: '#0f172a' },
      { label: t('vnstock.detail.avgVolume20d', 'KL TB 20D'), value: formatVolume(safeStockData.avg_volume_20d), color: '#0f172a' },
    ];
  }, [formatMarketCap, formatNumber, formatVolume, stockData, t]);

  // Professional loading skeleton - exact match with real layout
  if (loading) {
    return <StockDetailSkeleton />;
  }

  // Professional error state
  if (error) {
    return (
      <Fade in timeout={300}>
        <Box sx={{ 
          minHeight: '60vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 3
        }}>
          <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(244, 67, 54, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 3
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 40, color: colors.error.main }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: colors.text.primary, mb: 1 }}>
              {t('vnstock.detail.errorTitle', 'Không thể tải dữ liệu')}
            </Typography>
            <Typography variant="body1" sx={{ color: colors.text.secondary, mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => fetchStockData(false)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2
              }}
            >
              {t('common.retry', 'Thử lại')}
            </Button>
          </Box>
        </Box>
      </Fade>
    );
  }

  if (!stockData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: colors.text.secondary }}>
          {t('vnstock.detail.notFound', 'Không tìm thấy cổ phiếu')}
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box sx={{
        minHeight: '100vh',
        position: 'relative',
        contain: 'layout style paint',
        maxWidth: 1380,
        mx: 'auto',
        pb: 4,
      }}>
        <Box sx={{ px: { xs: 1.5, md: 3 }, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Tooltip title={currentLang === 'vi' ? 'Quay lại' : 'Back'} arrow>
              <IconButton
                onClick={handleBack}
                sx={{ bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={currentLang === 'vi' ? 'Làm mới dữ liệu' : 'Refresh data'} arrow>
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{ bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}
              >
                <RefreshIcon sx={{ fontSize: 19, animation: refreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              background: '#ffffff',
            }}
          >
            <Box
              sx={{
                px: { xs: 1.3, md: 1.7 },
                py: { xs: 1.15, md: 1.35 },
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: 'minmax(0,1.6fr) minmax(300px,0.8fr)' },
                gap: 1.15,
                alignItems: 'start',
              }}
            >
              <Stack spacing={0.95} sx={{ minWidth: 0 }}>
                <Box>
                  <Stack direction="row" spacing={0.65} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.35 }}>
                    <Typography sx={{ fontSize: { xs: '1.4rem', md: '1.65rem' }, fontWeight: 900, color: '#0f172a', letterSpacing: -0.7, lineHeight: 1 }}>
                      {ticker}
                    </Typography>
                    {stockData.exchange ? (
                      <Chip label={stockData.exchange} size="small" sx={{ height: 20, fontSize: '0.64rem', fontWeight: 800, bgcolor: '#eff6ff', color: '#2563eb' }} />
                    ) : null}
                    {stockData.industry_code ? (
                      <Chip label={stockData.industry_code} size="small" sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: '#f8fafc', color: '#475569', maxWidth: { xs: '100%', md: 220 }, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                    ) : null}
                    {recommendations?.style ? (
                      <Chip label={getStyleLabel(recommendations.style)} size="small" sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: alpha(colors.purple.main, 0.1), color: colors.purple.main }} />
                    ) : null}
                  </Stack>

                  <Typography sx={{ fontSize: { xs: '0.84rem', md: '0.9rem' }, fontWeight: 650, color: '#334155', lineHeight: 1.3 }}>
                    {stockData.company_name || 'N/A'}
                  </Typography>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.55 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {t('vnstock.detail.latestDate', 'Dữ liệu gần nhất')}: <Box component="span" sx={{ color: '#0f172a', fontWeight: 700 }}>{latestDateLabel}</Box>
                    </Typography>
                    {stockData.listing_date ? (
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {t('vnstock.detail.listedSince', 'Niêm yết từ')}: <Box component="span" sx={{ color: '#0f172a', fontWeight: 700 }}>{stockData.listing_date}</Box>
                      </Typography>
                    ) : null}
                    {recommendations?.as_of_date ? (
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {t('vnstock.detail.recommendationDate', 'Khuyến nghị theo ngày')}: <Box component="span" sx={{ color: '#0f172a', fontWeight: 700 }}>{recommendations.as_of_date}</Box>
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>

                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.3,
                    border: '1px solid #e8eef5',
                    bgcolor: '#fbfdff',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0,0.88fr) minmax(0,1.12fr)' },
                    gap: 1,
                    alignItems: 'start',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      {t('vnstock.detail.setupSnapshot', 'Tóm tắt setup')}
                    </Typography>
                    <Typography sx={{ mt: 0.35, fontSize: '0.82rem', fontWeight: 800, color: setupSummary.color, lineHeight: 1.25 }}>
                      {setupSummary.label}
                    </Typography>
                    <Stack direction="row" spacing={0.55} useFlexGap flexWrap="wrap" sx={{ mt: 0.55 }}>
                      {recommendations?.style ? (
                        <Chip
                          label={getStyleLabel(recommendations.style)}
                          size="small"
                          sx={{ height: 22, fontSize: '0.64rem', fontWeight: 800, bgcolor: alpha(colors.purple.main, 0.08), color: colors.purple.main }}
                        />
                      ) : null}
                      <Chip
                        label={`${t('vnstock.detail.signalQuality', 'Chất lượng')}: ${recommendations?.score != null ? recommendations.score.toFixed(1) : 'N/A'}`}
                        size="small"
                        sx={{ height: 22, fontSize: '0.64rem', fontWeight: 800, bgcolor: alpha(setupSummary.color, 0.08), color: setupSummary.color }}
                      />
                    </Stack>
                  </Box>

                  <Stack spacing={0.55}>
                    {heroReasons.length ? heroReasons.map((reason, index) => (
                      <Box key={`${reason}-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.65 }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: colors.success.main, mt: '7px', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.72rem', color: '#334155', lineHeight: 1.38 }}>
                          {reason}
                        </Typography>
                      </Box>
                    )) : (
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.4 }}>
                        {t('vnstock.detail.keepObserving', 'Tiếp tục theo dõi thêm dữ liệu xác nhận')}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>

              <Box
                sx={{
                  minWidth: 0,
                  p: 1.15,
                  borderRadius: 1.35,
                  border: `1px solid ${alpha(getPriceColor(), 0.14)}`,
                  bgcolor: alpha(getPriceColor(), 0.04),
                }}
              >
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      {t('vnstock.detail.currentPrice', 'Giá hiện tại')}
                    </Typography>
                    <Typography sx={{ mt: 0.18, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 900, color: getPriceColor(), fontFamily: 'monospace', lineHeight: 1 }}>
                      {formatStockPrice(latestPrice, 2)}
                    </Typography>
                  </Box>
                  {priceChange.change > 0 ? <TrendingUpIcon sx={{ color: getPriceColor(), fontSize: 20 }} />
                    : priceChange.change < 0 ? <TrendingDownIcon sx={{ color: getPriceColor(), fontSize: 20 }} />
                      : <TrendingFlatIcon sx={{ color: getPriceColor(), fontSize: 20 }} />}
                </Stack>
                <Divider sx={{ my: 0.7, borderColor: alpha(getPriceColor(), 0.12) }} />
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ gap: 1 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 700 }}>
                    {t('vnstock.detail.sessionMove', 'Biến động phiên')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: getPriceColor(), fontFamily: 'monospace', textAlign: 'right' }}>
                    {priceChange.change > 0 ? '+' : ''}{formatStockPrice(priceChange.change, 2)} ({priceChange.percent > 0 ? '+' : ''}{formatStockPrice(priceChange.percent, 2)}%)
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    mt: 0.9,
                    pt: 0.8,
                    borderTop: `1px dashed ${alpha(getPriceColor(), 0.16)}`,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 0.8,
                  }}
                >
                  {[
                    { label: t('vnstock.detail.rangePosition', 'Vị trí 20 phiên'), value: marketContext.rangePosition != null ? `${marketContext.rangePosition.toFixed(0)}%` : 'N/A', color: '#0f172a' },
                    { label: t('vnstock.detail.volumeRatio', 'Vol/TB20'), value: marketContext.volumeRatio != null ? `${marketContext.volumeRatio.toFixed(2)}x` : 'N/A', color: marketContext.volumeRatio >= 1 ? colors.success.main : '#0f172a' },
                  ].map((item) => (
                    <Box key={item.label}>
                      <Typography sx={{ fontSize: '0.56rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ mt: 0.15, fontSize: '0.82rem', fontWeight: 800, color: item.color, lineHeight: 1.1 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  gridColumn: '1 / -1',
                  mt: 0.1,
                  px: { xs: 0.05, md: 0.1 },
                  pt: 0.8,
                  borderTop: '1px solid #eef2f7',
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
                  columnGap: 1.15,
                  rowGap: 0.6,
                }}
              >
                {[
                  { label: t('vnstock.detail.marketCap', 'Vốn hóa'), value: `${formatMarketCap(stockData.market_cap_bn).value}${formatMarketCap(stockData.market_cap_bn).unit ? ` ${formatMarketCap(stockData.market_cap_bn).unit}` : ''}`, color: colors.purple.main },
                  { label: t('vnstock.detail.return5d', 'Hiệu quả 5 phiên'), value: marketContext.return5d != null ? `${marketContext.return5d > 0 ? '+' : ''}${marketContext.return5d.toFixed(2)}%` : 'N/A', color: marketContext.return5d >= 0 ? colors.success.main : colors.error.main },
                  { label: t('vnstock.detail.return20d', 'Hiệu quả 20 phiên'), value: marketContext.return20d != null ? `${marketContext.return20d > 0 ? '+' : ''}${marketContext.return20d.toFixed(2)}%` : 'N/A', color: marketContext.return20d >= 0 ? colors.success.main : colors.error.main },
                  { label: t('vnstock.detail.rangePosition', 'Vị trí trong biên 20 phiên'), value: marketContext.rangePosition != null ? `${marketContext.rangePosition.toFixed(0)}%` : 'N/A', color: '#0f172a' },
                  { label: t('vnstock.detail.volumeRatio', 'Volume so với TB20'), value: marketContext.volumeRatio != null ? `${marketContext.volumeRatio.toFixed(2)}x` : 'N/A', color: marketContext.volumeRatio >= 1 ? colors.success.main : '#0f172a' },
                  { label: t('vnstock.detail.recommendationScore', 'Điểm setup'), value: recommendations?.score != null ? recommendations.score.toFixed(1) : 'N/A', color: recommendations?.score >= 80 ? colors.success.main : recommendations?.score >= 65 ? colors.warning.main : '#0f172a' },
                ].map((item) => (
                  <Box key={item.label} sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.28 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ mt: 0.18, fontSize: '0.88rem', fontWeight: 800, color: item.color, lineHeight: 1.15 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.7fr) minmax(320px, 0.95fr)' },
              gap: 1.5,
              alignItems: 'start',
            }}
          >
            <DetailCard
              title={t('vnstock.detail.priceChart', 'Biểu đồ giá')}
              subtitle={t('vnstock.detail.chartSubtitle', 'Đọc cấu trúc giá, volume và vị trí hiện tại')}
              action={(
                <Stack direction="row" spacing={1} alignItems="center">
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={chartType}
                    onChange={(_, value) => value && setChartType(value)}
                    sx={{ '& .MuiToggleButton-root': { px: 1.1, py: 0.45, fontSize: '0.7rem', fontWeight: 700, textTransform: 'none' } }}
                  >
                    <ToggleButton value="candlestick">
                      <BarChartIcon sx={{ fontSize: 14, mr: 0.6 }} />
                      OHLC
                    </ToggleButton>
                    <ToggleButton value="line">
                      <TimelineIcon sx={{ fontSize: 14, mr: 0.6 }} />
                      Line
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Chip label="Vol" onClick={() => setShowVolume(!showVolume)} size="small" variant={showVolume ? 'filled' : 'outlined'} sx={{ fontWeight: 700, height: 26 }} />
                  <Chip label="MA" onClick={() => setShowIndicators(!showIndicators)} size="small" variant={showIndicators ? 'filled' : 'outlined'} sx={{ fontWeight: 700, height: 26 }} />
                </Stack>
              )}
              sx={{ overflow: 'hidden' }}
            >
              <Stack spacing={1.1}>
                <Stack direction="row" spacing={1.35} useFlexGap flexWrap="wrap">
                  {[
                    { label: t('vnstock.detail.high20d', 'Đỉnh 20 phiên'), value: marketContext.high20 != null ? formatStockPrice(marketContext.high20, 2) : 'N/A', color: colors.success.main },
                    { label: t('vnstock.detail.low20d', 'Đáy 20 phiên'), value: marketContext.low20 != null ? formatStockPrice(marketContext.low20, 2) : 'N/A', color: colors.error.main },
                    { label: t('vnstock.detail.avgVolume20d', 'KL TB 20D'), value: formatVolume(marketContext.avgVol20), color: '#0f172a' },
                  ].map((item) => (
                    <Box key={item.label}>
                      <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ mt: 0.18, fontSize: '0.88rem', fontWeight: 800, color: item.color, fontFamily: item.label.includes('KL') ? 'inherit' : 'monospace' }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                {/* Time range selector */}
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  {[
                    { label: '3T', days: 90 },
                    { label: '6T', days: 180 },
                    { label: '1N', days: 365 },
                    { label: '3N', days: 1095 },
                    { label: '5N', days: 1825 },
                    { label: 'Tất cả', days: 3650 },
                  ].map(({ label, days }) => (
                    <Chip
                      key={label}
                      label={label}
                      size="small"
                      onClick={async () => {
                        if (days === chartDays) return;
                        setChartDays(days);
                        setChartLoading(true);
                        try {
                          const res = await stocksAPI.getStockPrices(ticker, days);
                          const sorted = [...(res.data || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
                          setPriceHistory(sorted);
                        } catch (e) {
                          console.error('Failed to load price history:', e);
                        } finally {
                          setChartLoading(false);
                        }
                      }}
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        height: 22,
                        cursor: 'pointer',
                        bgcolor: chartDays === days ? '#0f172a' : 'transparent',
                        color: chartDays === days ? '#fff' : '#64748b',
                        border: '1px solid',
                        borderColor: chartDays === days ? '#0f172a' : '#e2e8f0',
                        '&:hover': { bgcolor: chartDays === days ? '#0f172a' : '#f1f5f9' },
                      }}
                    />
                  ))}
                </Stack>
                <Box sx={{ height: { xs: 290, md: 390 }, position: 'relative' }}>
                  {chartLoading && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1, borderRadius: 1 }}>
                      <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>Đang tải...</Typography>
                    </Box>
                  )}
                  {priceHistory.length > 0 ? (
                    <TradingViewChart
                      priceHistory={priceHistory}
                      ticker={ticker}
                      height={360}
                      showVolume={showVolume}
                      showIndicators={showIndicators}
                      chartType={chartType}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography sx={{ color: '#64748b' }}>
                        {t('vnstock.detail.noData', 'Không có dữ liệu')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            </DetailCard>

            <Stack spacing={1.15}>
              <DetailCard
                title={t('vnstock.detail.setupSnapshot', 'Luận điểm nhanh')}
                subtitle={t('vnstock.detail.setupSnapshotSubtitle', 'Một panel đủ để quyết định có nên đọc sâu tiếp hay không')}
              >
                <Stack spacing={1.05}>
                  <Box
                    sx={{
                      p: 1.05,
                      borderRadius: 1.25,
                      background: `linear-gradient(135deg, ${setupSummary.bg} 0%, #ffffff 100%)`,
                      border: `1px solid ${alpha(setupSummary.color, 0.14)}`,
                    }}
                  >
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: setupSummary.color, lineHeight: 1.2 }}>
                          {setupSummary.label}
                        </Typography>
                        <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap" sx={{ mt: 0.55 }}>
                          {recommendations?.style ? (
                            <Chip
                              label={getStyleLabel(recommendations.style)}
                              size="small"
                              sx={{ height: 22, fontSize: '0.64rem', fontWeight: 800, bgcolor: '#fff', color: colors.purple.main, border: `1px solid ${alpha(colors.purple.main, 0.14)}` }}
                            />
                          ) : null}
                          {marketContext.rangePosition != null ? (
                            <Chip
                              label={`${t('vnstock.detail.rangePosition', 'Vị trí 20 phiên')}: ${marketContext.rangePosition.toFixed(0)}%`}
                              size="small"
                              sx={{ height: 22, fontSize: '0.64rem', fontWeight: 700, bgcolor: '#fff', color: '#475569', border: '1px solid #e2e8f0' }}
                            />
                          ) : null}
                          {recommendations?.marketStage?.stage ? (
                            <Chip
                              label={getMarketStageLabel(t, recommendations.marketStage.stage)}
                              size="small"
                              sx={{ height: 22, fontSize: '0.64rem', fontWeight: 700, bgcolor: alpha(marketStageAccent, 0.08), color: marketStageAccent, border: `1px solid ${alpha(marketStageAccent, 0.14)}` }}
                            />
                          ) : null}
                          {typeof recommendations?.marketStage?.exposure_target_pct === 'number' ? (
                            <Chip
                              label={`${recommendations.marketStage.exposure_target_pct.toFixed(1)}%`}
                              size="small"
                              sx={{ height: 22, fontSize: '0.64rem', fontWeight: 700, bgcolor: '#fff', color: '#475569', border: '1px solid #e2e8f0' }}
                            />
                          ) : null}
                        </Stack>
                      </Box>
                      <Box
                        sx={{
                          px: 0.9,
                          py: 0.55,
                          borderRadius: 99,
                          bgcolor: '#fff',
                          border: `1px solid ${alpha(setupSummary.color, 0.12)}`,
                          minWidth: 74,
                          textAlign: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Typography sx={{ fontSize: '0.54rem', color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.35 }}>
                          {t('vnstock.detail.signalQuality', 'Chất lượng')}
                        </Typography>
                        <Typography sx={{ mt: 0.12, fontSize: '0.92rem', fontWeight: 900, color: setupSummary.color, lineHeight: 1 }}>
                          {recommendations?.score != null ? recommendations.score.toFixed(1) : 'N/A'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  {recommendationReasons.length ? (
                    <Stack spacing={0.65}>
                      {recommendationReasons.slice(0, 3).map((reason, index) => (
                        <Box key={`${reason}-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                          <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: alpha(colors.success.main, 0.1), color: colors.success.main, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.64rem', fontWeight: 900, mt: '1px', flexShrink: 0 }}>
                            {index + 1}
                          </Box>
                          <Typography sx={{ fontSize: '0.73rem', color: '#334155', lineHeight: 1.42 }}>
                            {reason}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.45 }}>
                      {t('vnstock.detail.noRecommendationReasons', 'Chưa có lý do khuyến nghị nổi bật để hiển thị cho mã này.')}
                    </Typography>
                  )}

                  {marketStageSummary ? (
                    <Box
                      sx={{
                        p: 0.9,
                        borderRadius: 1.1,
                        bgcolor: alpha(marketStageAccent, 0.04),
                        border: `1px solid ${alpha(marketStageAccent, 0.10)}`,
                      }}
                    >
                      <Stack direction="row" spacing={0.8} alignItems="flex-start">
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: marketStageAccent, mt: 0.1, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.72rem', color: '#334155', lineHeight: 1.45 }}>
                            {marketStageSummary}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ) : null}

                  {marketStageOverlay.visible ? (
                    <Tooltip title={marketStageOverlay.text} arrow placement="top">
                      <Box>
                        <Chip
                          icon={<InfoOutlinedIcon sx={{ fontSize: '0.9rem !important' }} />}
                          label={`${t('vnstock.detail.marketStageAdjustment', 'Điều chỉnh theo pha thị trường')} ${marketStageOverlay.pct > 0 ? '+' : ''}${marketStageOverlay.pct.toFixed(1)}%`}
                          size="small"
                          sx={{
                            height: 24,
                            maxWidth: '100%',
                            fontSize: '0.64rem',
                            fontWeight: 700,
                            bgcolor: marketStageOverlay.positive ? alpha(colors.success.main, 0.06) : '#fef2f2',
                            color: marketStageOverlay.positive ? colors.success.main : '#b91c1c',
                            border: `1px solid ${marketStageOverlay.positive ? alpha(colors.success.main, 0.14) : '#fecaca'}`,
                            '& .MuiChip-label': {
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  ) : null}

                  <Stack direction="row" spacing={1.2} useFlexGap flexWrap="wrap">
                    {setupEvidence.map((item) => (
                      <Box key={item.label} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.45 }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>
                          {item.label}:
                        </Typography>
                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: item.accent }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  <Divider sx={{ borderColor: '#eef2f7' }} />

                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.35, mb: 0.7 }}>
                      {t('vnstock.detail.keyMetrics', 'Snapshot tài chính')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, columnGap: 1.2, rowGap: 0.65 }}>
                      {financialSnapshot.map((item) => (
                        <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, pb: 0.3, borderBottom: '1px solid #f1f5f9' }}>
                          <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700 }}>
                            {item.label}
                          </Typography>
                          <Typography sx={{ fontSize: '0.82rem', color: item.color, fontWeight: 800, fontFamily: 'monospace', textAlign: 'right' }}>
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Stack>
              </DetailCard>

              <DetailCard
                title={t('vnstock.detail.aiAnalysis', 'Góc nhìn AI')}
                subtitle={t('vnstock.detail.aiAnalysisSubtitle', 'Tổng hợp diễn giải nếu AI pipeline có dữ liệu cho mã này')}
                action={<AutoAwesomeIcon sx={{ fontSize: 18, color: colors.purple.main }} />}
              >
                {aiExplanations.length ? (
                  <Stack spacing={1}>
                    {aiExplanations.map((item) => (
                      <Box key={item.style} sx={{ p: 1.1, borderRadius: 1.3, bgcolor: alpha(colors.purple.main, 0.05), border: `1px solid ${alpha(colors.purple.main, 0.1)}` }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.65 }}>
                          <Chip label={getStyleLabel(item.style)} size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 800, bgcolor: alpha(colors.purple.main, 0.1), color: colors.purple.main }} />
                        </Stack>
                        <Typography sx={{ fontSize: '0.78rem', color: '#334155', lineHeight: 1.5 }}>
                          {item.explanation}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.5 }}>
                    {t('vnstock.detail.noAiAnalysis', 'Hiện chưa có lớp diễn giải AI đủ rõ cho mã này. Khi pipeline AI có output, phần này sẽ dùng để giải thích thêm về bối cảnh và thesis.') }
                  </Typography>
                )}
              </DetailCard>
            </Stack>
          </Box>

          {signalGroups.length ? (
            <DetailCard
              title={t('vnstock.detail.signalDashboard', 'Dashboard tín hiệu')}
              subtitle={t('vnstock.detail.signalDashboardSubtitle', 'Nhóm lại tín hiệu để đọc nhanh thay vì nhìn một danh sách kỹ thuật rời rạc')}
              action={<ShowChartIcon sx={{ fontSize: 18, color: colors.valueConfirmation.main }} />}
            >
              <Stack spacing={1.2}>
                {signalGroups.map((group) => (
                  <Box key={group.key}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.35, mb: 0.75 }}>
                      {group.title}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                      {group.items.map((item) => (
                        <Box
                          key={item.key}
                          sx={{
                            p: 1,
                            borderRadius: 1.2,
                            border: '1px solid #eef2f7',
                            bgcolor: '#fff',
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700 }}>
                                {item.label}
                              </Typography>
                              <Typography sx={{ mt: 0.28, fontSize: '0.84rem', color: '#0f172a', fontWeight: 800, lineHeight: 1.3 }}>
                                {item.displayValue}
                              </Typography>
                            </Box>
                            <Chip
                              label={item.tone.sentiment === 'positive'
                                ? (currentLang === 'vi' ? 'Tích cực' : 'Positive')
                                : item.tone.sentiment === 'negative'
                                  ? (currentLang === 'vi' ? 'Thận trọng' : 'Caution')
                                  : (currentLang === 'vi' ? 'Trung tính' : 'Neutral')}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.62rem',
                                fontWeight: 800,
                                color: item.tone.color,
                                bgcolor: item.tone.bg,
                                flexShrink: 0,
                              }}
                            />
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </DetailCard>
          ) : null}
        </Box>
      </Box>
    </Fade>
  );
};

export default StockDetail;
